import { Fragment, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
  SparklesIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { screenByBoard, webPath, webScreens, type Screen } from '@screens';
import { useSidebar } from '../../stores/uiStore';
import { useAuth } from '../../hooks/useAuth';
import { locateScreen } from '../../config/navigation';
import { useBoardChrome } from '../../canvas/dc/boardChrome';
import { useRealtimeStatus } from '../../stores/realtimeStatusStore';
import '../../canvas/tokens.css';
import '../../canvas/chrome.css';

/** Link target for a canvas board, falling back to the catalog if the board is missing. */
function boardHref(board: string): string {
  const s = screenByBoard(board);
  return s ? webPath(s) : '/platform/screens';
}

/** Placeholder site list. Real sites come from the facilities API once it is connected. */
const SITES = [
  { code: 'DC-01', name: 'DC-01', note: 'Placeholder site' },
  { code: 'DC-02', name: 'DC-02', note: 'Placeholder site' },
];

const MAX_RESULTS = 12;

/** Lower-cased search text per screen, built once. */
const searchIndex = webScreens.map((s) => ({
  s,
  text: [s.id, s.name, s.heading, s.board, s.pageName, s.module].filter(Boolean).join(' ').toLowerCase(),
}));

function searchScreens(q: string): Screen[] {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const out: Screen[] = [];
  const seen = new Set<string>();
  for (const { s, text } of searchIndex) {
    if (terms.every((t) => text.includes(t))) {
      const href = webPath(s);
      if (seen.has(href)) continue;
      seen.add(href);
      out.push(s);
      if (out.length >= MAX_RESULTS) break;
    }
  }
  return out;
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}

export default function Header() {
  const { isOpen, toggle } = useSidebar();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const activeBoard = useBoardChrome((s) => s.board);
  // a mounted 1:1 design board names the screen (also on /design/:board and ?view=design)
  const boardScreen = activeBoard ? screenByBoard(activeBoard) ?? null : null;
  const located = useMemo(
    () => (boardScreen ? locateScreen(webPath(boardScreen)) : locateScreen(pathname)),
    [boardScreen, pathname],
  );
  const screen = boardScreen ?? located?.screen ?? null;
  const [site, setSite] = useState(SITES[0]);

  return (
    <header className="cv ch-top">
      <div className="ch-bar">
        <button
          type="button"
          onClick={toggle}
          className="ch-ib ch-menu-btn"
          aria-label="Open navigation"
          aria-expanded={isOpen}
          aria-controls="primary-navigation-drawer"
        >
          <Bars3Icon aria-hidden="true" />
        </button>

        {/* Site switcher (placeholder sites; no API yet) */}
        <Menu as="div" style={{ position: 'relative' }}>
          <Menu.Button className="ch-fac" aria-label={`Site: ${site.name}. Change site`}>
            <span className="ch-site">
              <BuildingOffice2Icon aria-hidden="true" />
            </span>
            <span className="ch-fac-t">
              <span className="ch-fac-n cv-mono">{site.code}</span>
              <span className="ch-fac-s">Site switcher · placeholder</span>
            </span>
            <ChevronDownIcon className="ch-chev" aria-hidden="true" />
          </Menu.Button>
          <Transition as={Fragment} leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Menu.Items className="ch-pop" style={{ left: 0, width: 260 }}>
              <div className="ch-pop-l">Sites</div>
              <div className="ch-pop-b" style={{ paddingTop: 0 }}>
                {SITES.map((s) => (
                  <Menu.Item key={s.code}>
                    {({ active }) => (
                      <button
                        type="button"
                        className="ch-row"
                        data-active={active ? 'true' : undefined}
                        aria-current={s.code === site.code ? 'true' : undefined}
                        onClick={() => setSite(s)}
                      >
                        <span className="ch-row-m">
                          <span className="ch-row-t cv-mono">{s.code}</span>
                          <span className="ch-row-s">{s.note}</span>
                        </span>
                        {s.code === site.code && <CheckCircleIcon aria-hidden="true" />}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
              <div className="ch-pop-f">Sites are placeholders until facilities are connected.</div>
            </Menu.Items>
          </Transition>
        </Menu>

        <span className="ch-sep" aria-hidden="true" />

        {screen && screen.nav.length > 0 ? (
          <ModuleCrumb screen={screen} />
        ) : located ? (
          <nav className="ch-crumb" aria-label="Breadcrumb">
            <span className="ch-crumb-m">{located.groupLabel}</span>
            <span className="ch-crumb-x" aria-hidden="true">
              /
            </span>
            <span className="ch-crumb-m">{located.pageLabel}</span>
            <span className="ch-crumb-x" aria-hidden="true">
              /
            </span>
            <span className="ch-crumb-s" aria-current="page">
              {located.screen.name}
            </span>
          </nav>
        ) : null}

        <ScreenSearch />

        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 'none' }}>
          <LiveChip />

          <Link to={boardHref('CreateMenu')} className="ch-create">
            <PlusIcon aria-hidden="true" />
            <span className="ch-create-t">Create</span>
            <span className="cv-sr">(opens the create menu)</span>
          </Link>

          <Link to={boardHref('AskReport')} className="ch-ib ch-ai" aria-label="Ask" title="Ask">
            <SparklesIcon aria-hidden="true" />
          </Link>

          <Link to={boardHref('Approvals')} className="ch-ib ch-wide" aria-label="Approvals, count not available" title="Approvals">
            <CheckCircleIcon aria-hidden="true" />
            <span className="ch-count" aria-hidden="true">
              —
            </span>
          </Link>

          <Link to={boardHref('Feed')} className="ch-ib" aria-label="Notifications feed, count not available" title="Notifications">
            <BellIcon aria-hidden="true" />
            <span className="ch-count" aria-hidden="true">
              —
            </span>
          </Link>

          <Menu as="div" style={{ position: 'relative' }}>
            <Menu.Button className="ch-av" aria-label={`Account menu${user?.fullName ? ` for ${user.fullName}` : ''}`}>
              {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <span aria-hidden="true">{initials(user?.fullName)}</span>}
            </Menu.Button>
            <Transition as={Fragment} leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
              <Menu.Items className="ch-pop" style={{ right: 0, width: 260 }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--cv-line2)' }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{user?.fullName ?? 'Signed in'}</p>
                  {user?.role ? (
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--cv-muted)', textTransform: 'capitalize' }}>{user.role}</p>
                  ) : null}
                </div>
                <div className="ch-pop-b">
                  <Menu.Item>
                    {({ active }) => (
                      <Link to="/platform/screens" className="ch-row" data-active={active ? 'true' : undefined}>
                        <Squares2X2Icon aria-hidden="true" />
                        All screens
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link to="/settings/users" className="ch-row" data-active={active ? 'true' : undefined}>
                        <Cog6ToothIcon aria-hidden="true" />
                        Administration
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={logout}
                        className="ch-row ch-row-danger"
                        data-active={active ? 'true' : undefined}
                      >
                        <ArrowRightOnRectangleIcon aria-hidden="true" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
}

/**
 * The canvas TopNav crumb (module / screen, with a menu of the module's sibling screens from the
 * board's `<dc-import name="TopNav" nav=...>` list, extracted into the registry as `screen.nav`).
 * The rest of the canvas TopNav (site switcher, search, create, ask, approvals, feed, account) is
 * already this header, so only the per-module navigation is carried over.
 */
function ModuleCrumb({ screen }: { screen: Screen }) {
  const module = screen.module || screen.pageName || 'This area';
  return (
    <nav className="ch-crumb ch-crumb-nav" aria-label="Breadcrumb">
      <Menu as="div" style={{ position: 'relative', minWidth: 0, display: 'flex' }}>
        <Menu.Button className="ch-crumb-btn" aria-label={`${module}, ${screen.name}. Other screens in this area`}>
          <span className="ch-crumb-m">{module}</span>
          <span className="ch-crumb-x" aria-hidden="true">
            /
          </span>
          <span className="ch-crumb-s" aria-current="page">
            {screen.name}
          </span>
          <ChevronDownIcon className="ch-chev" aria-hidden="true" />
        </Menu.Button>
        <Transition as={Fragment} leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
          <Menu.Items className="ch-pop" style={{ left: 0, width: 340 }}>
            <div className="ch-pop-l">{module}</div>
            <div className="ch-pop-b" style={{ paddingTop: 0 }}>
              {screen.nav.map((n) => {
                const here = n.board === screen.board;
                return (
                  <Menu.Item key={`${n.board}:${n.label}`}>
                    {({ active }) => (
                      <Link
                        to={boardHref(n.board)}
                        className="ch-row"
                        data-active={active ? 'true' : undefined}
                        aria-current={here ? 'page' : undefined}
                        style={{ minHeight: 40, fontWeight: here ? 600 : undefined }}
                      >
                        <span className="ch-row-m">
                          <span className="ch-row-t">{n.label}</span>
                        </span>
                        {here && <CheckIcon aria-hidden="true" />}
                      </Link>
                    )}
                  </Menu.Item>
                );
              })}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </nav>
  );
}

/** Live-updates status of the shell's realtime subscription (the canvas TopNav connection chip). */
function LiveChip() {
  const status = useRealtimeStatus((s) => s.status);
  const retryInMs = useRealtimeStatus((s) => s.retryInMs);
  if (status === 'off') return null;
  const label = status === 'live' ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Live updates unavailable';
  const title =
    status === 'unavailable'
      ? `Live updates could not connect. Pages still load and refresh normally${retryInMs ? `; retrying in ${Math.round(retryInMs / 1000)} s` : ''}.`
      : status === 'live'
        ? 'Live updates connected'
        : 'Connecting to live updates';
  return (
    <span className={`ch-conn ch-conn-${status}`} role="status" title={title} data-testid="live-status">
      <span className="ch-conn-dot" aria-hidden="true" />
      {label}
    </span>
  );
}

/** Global search over canvas screens (client-side, by name / id / module). A combobox with a listbox of links. */
function ScreenSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const results = useMemo(() => searchScreens(q), [q]);
  const showList = open && q.trim().length > 0;

  useEffect(() => setActive(0), [q]);

  // "/" or Ctrl/Cmd+K focuses search, unless typing in a field.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const go = (s: Screen) => {
    setOpen(false);
    setQ('');
    navigate(webPath(s));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const s = results[active];
      if (s) {
        e.preventDefault();
        go(s);
      }
    } else if (e.key === 'Escape') {
      if (q) setQ('');
      else inputRef.current?.blur();
      setOpen(false);
    }
  };

  return (
    <div className="ch-search-wrap" ref={wrapRef}>
      <div className="ch-search">
        <MagnifyingGlassIcon aria-hidden="true" />
        <label htmlFor={`${listId}-input`} className="cv-sr">
          Search screens
        </label>
        <input
          ref={inputRef}
          id={`${listId}-input`}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={`${listId}-list`}
          aria-autocomplete="list"
          aria-activedescendant={showList && results[active] ? `${listId}-opt-${active}` : undefined}
          placeholder="Search screens by name or ID"
          autoComplete="off"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <span className="ch-kbd" aria-hidden="true">
          /
        </span>
      </div>
      {showList && (
        <div className="ch-pop" style={{ left: 0, right: 0 }}>
          <div className="ch-pop-l">{results.length ? 'Screens' : 'No matches'}</div>
          <ul id={`${listId}-list`} role="listbox" aria-label="Matching screens" className="ch-pop-b" style={{ listStyle: 'none', margin: 0, paddingTop: 0 }}>
            {results.length === 0 ? (
              <li role="option" aria-selected="false" aria-disabled="true" className="ch-row" data-disabled="true">
                No screens match “{q.trim()}”. Try a screen name such as “Wave planning” or an ID like SCR-090.
              </li>
            ) : (
              results.map((s, i) => (
                <li key={s.board} id={`${listId}-opt-${i}`} role="option" aria-selected={i === active}>
                  <Link
                    to={webPath(s)}
                    className="ch-row"
                    data-active={i === active ? 'true' : undefined}
                    tabIndex={-1}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      setOpen(false);
                      setQ('');
                    }}
                  >
                    <span className="ch-row-m">
                      <span className="ch-row-t">{s.name}</span>
                      <span className="ch-row-s">
                        {[s.id, s.pageName].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {s.wiredWeb && <span className="ch-live">Live</span>}
                  </Link>
                </li>
              ))
            )}
          </ul>
          <div className="ch-pop-f">
            <Link to="/platform/screens" style={{ color: 'var(--cv-fg)' }} onClick={() => setOpen(false)}>
              Browse all screens
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
